/**
 * Broker Ticket — live order entry through the user's own MetaTrader terminal
 * via the mt-ea bridge. Beta-only (registered with betaPreview in widgets.ts).
 *
 * Market orders with optional SL/TP. Companion widget: Broker Positions.
 * Auto-switch: Off / On click / Follow feed — driven by headline tags via
 * BrokerStore.publishTags (see stores/BrokerStore.ts header).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import clsx from "clsx";
import { createOrder, getOrder, getQuote, TradeCommand } from "@/shared/api/mt-ea";
import { newIdempotencyKey } from "@/shared/api/mt-ea/idempotency";
import { AutoSwitchMode, useBrokerStore } from "@/stores/BrokerStore";
import { useBrokerWidgetSymbol } from "@/hooks/useBrokerWidgetSymbol";

const AUTO_SWITCH_LABELS: Record<AutoSwitchMode, string> = {
  off: "Off",
  click: "On click",
  feed: "Follow feed",
};

const fmtPrice = (value: number | null | undefined) => {
  if (value == null) return "—";
  return value >= 100 ? value.toFixed(2) : value.toFixed(5);
};

const BrokerTicket = ({ widgetCompositeId }: { widgetCompositeId?: string }) => {
  const { account, accountLoaded, symbolMap, lastCommand, connect, disconnect, quoteFor } =
    useBrokerStore();
  // Symbol + auto-switch are per widget instance, so two tickets can track
  // different assets (one following the feed, one pinned).
  const {
    activeSymbol,
    setActiveSymbol,
    autoSwitchMode,
    setAutoSwitchMode,
    setFormBusy,
  } = useBrokerWidgetSymbol(widgetCompositeId);

  const [lots, setLots] = useState("0.10");
  const [useSl, setUseSl] = useState(false);
  const [useTp, setUseTp] = useState(false);
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [pendingOrderId, setPendingOrderId] = useState<number | null>(null);
  const pendingTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    connect();
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register (and keep alive) the quote watch for the active symbol.
  useEffect(() => {
    if (!account) return;
    let cancelled = false;
    const register = () => {
      getQuote(activeSymbol).catch((error) => {
        if (!cancelled) console.error("[broker-ticket] quote watch failed:", error);
      });
    };
    register();
    const interval = setInterval(register, 120_000); // watch TTL is 300s
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [account, activeSymbol]);

  // Resolve the pending order from SSE command events.
  useEffect(() => {
    if (!pendingOrderId || !lastCommand || lastCommand.id !== pendingOrderId) return;
    settleOrder(lastCommand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCommand, pendingOrderId]);

  const settleOrder = (command: TradeCommand) => {
    if (pendingTimeout.current) clearTimeout(pendingTimeout.current);
    setPendingOrderId(null);
    if (command.status === "filled") {
      toast.success(
        `${command.type === "market_buy" ? "Bought" : "Sold"} ${command.lots} lots ${command.symbol} @ ${fmtPrice(command.result_price)}`
      );
    } else {
      toast.error(`Order ${command.status}: ${command.error_text || "no reason given"}`);
    }
  };

  const submit = async (type: "market_buy" | "market_sell") => {
    const lotsNumber = parseFloat(lots);
    if (!lotsNumber || lotsNumber <= 0) {
      toast.error("Enter a lot size");
      return;
    }
    try {
      const command = await createOrder({
        idempotency_key: newIdempotencyKey(),
        type,
        symbol: activeSymbol,
        lots: lotsNumber,
        sl: useSl && sl ? parseFloat(sl) : null,
        tp: useTp && tp ? parseFloat(tp) : null,
      });
      setPendingOrderId(command.id);
      // SSE resolves this almost always; fall back to one status check.
      pendingTimeout.current = setTimeout(async () => {
        try {
          const latest = await getOrder(command.id);
          if (latest.status !== "pending" && latest.status !== "delivered") {
            settleOrder(latest);
          } else {
            setPendingOrderId(null);
            toast.error("No response from the terminal — check Broker Positions before retrying.");
          }
        } catch {
          setPendingOrderId(null);
        }
      }, 20_000);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Order failed to submit");
    }
  };

  const tradableSymbols = useMemo(
    () =>
      Object.entries(symbolMap)
        .filter(([, brokerSymbol]) => brokerSymbol !== null)
        .map(([canonical]) => canonical)
        .sort(),
    [symbolMap]
  );

  const quote = quoteFor(activeSymbol);
  const spread = quote ? quote.ask - quote.bid : null;
  const state = account?.state;
  const disabled =
    !account?.connected ||
    account.kill_switch ||
    account.live_blocked ||
    pendingOrderId !== null;

  // --- unpaired / loading states ---
  if (!accountLoaded) {
    return <div className="p-4 text-xs text-gray-400">Loading broker connection…</div>;
  }
  if (!account) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Connect your MetaTrader account to trade from this widget.
        </p>
        <Link
          to="/settings/broker"
          className="rounded-md bg-squawk-blue px-3 py-1.5 text-xs font-semibold text-white hover:bg-squawk-blue-light transition"
        >
          Connect a broker
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 p-3 text-gray-900 dark:text-white">
      {/* connection / kill-switch banners */}
      {!account.connected && (
        <div className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
          Terminal offline — open MetaTrader with the NewsquawkBridge EA to enable trading.
        </div>
      )}
      {account.kill_switch && (
        <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
          Trading is paused on this connection by Newsquawk support.
        </div>
      )}
      {account.live_blocked && (
        <div className="rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500">
          Terminal is on a live account ({account.broker_server}). Enable live trading
          explicitly in{" "}
          <Link to="/settings/broker" className="font-semibold underline">
            Settings → Broker
          </Link>{" "}
          or switch MetaTrader to a demo login.
        </div>
      )}
      {account.live_server && account.allow_live && (
        <div className="rounded-md bg-amber-500/10 px-3 py-1.5 text-center text-1.5xs font-semibold tracking-wide text-amber-600 dark:text-amber-400">
          LIVE ACCOUNT — ORDERS USE REAL MONEY
        </div>
      )}

      {/* symbol + auto-switch */}
      <div className="flex items-center justify-between gap-2">
        <select
          value={activeSymbol}
          onChange={(e) => setActiveSymbol(e.target.value)}
          className="rounded-md border-0 bg-gray-100 dark:bg-white/10 py-1 pl-2 pr-8 text-sm font-semibold focus:ring-2 focus:ring-squawk-blue"
        >
          {!tradableSymbols.includes(activeSymbol) && (
            <option value={activeSymbol}>{activeSymbol}</option>
          )}
          {tradableSymbols.map((symbol) => (
            <option key={symbol} value={symbol}>
              {symbol}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-1.5xs text-gray-500 dark:text-gray-400">
          Auto-switch
          <select
            value={autoSwitchMode}
            onChange={(e) => setAutoSwitchMode(e.target.value as AutoSwitchMode)}
            className="rounded-md border-0 bg-gray-100 dark:bg-white/10 py-0.5 pl-1.5 pr-6 text-1.5xs focus:ring-2 focus:ring-squawk-blue"
            title="Let headline-feed tags change the loaded asset"
          >
            {(Object.keys(AUTO_SWITCH_LABELS) as AutoSwitchMode[]).map((mode) => (
              <option key={mode} value={mode}>
                {AUTO_SWITCH_LABELS[mode]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* bid/ask */}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => submit("market_sell")}
          className={clsx(
            "flex flex-col items-center rounded-lg py-2 transition",
            "bg-red-500/10 hover:bg-red-500/20 disabled:opacity-40 disabled:hover:bg-red-500/10"
          )}
        >
          <span className="text-1.5xs text-gray-500 dark:text-gray-400">Sell</span>
          <span className="text-lg font-semibold tabular-nums text-red-500">
            {fmtPrice(quote?.bid)}
          </span>
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => submit("market_buy")}
          className={clsx(
            "flex flex-col items-center rounded-lg py-2 transition",
            "bg-green-500/10 hover:bg-green-500/20 disabled:opacity-40 disabled:hover:bg-green-500/10"
          )}
        >
          <span className="text-1.5xs text-gray-500 dark:text-gray-400">Buy</span>
          <span className="text-lg font-semibold tabular-nums text-green-500">
            {fmtPrice(quote?.ask)}
          </span>
        </button>
      </div>
      <div className="flex justify-between text-1.5xs text-gray-500 dark:text-gray-400">
        <span>Spread</span>
        <span className="tabular-nums">{spread != null ? fmtPrice(spread) : "—"}</span>
      </div>

      {/* lots + sl/tp */}
      <div
        onFocusCapture={() => setFormBusy(true)}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setFormBusy(false);
        }}
        className="space-y-2"
      >
        <div className="flex items-center gap-2">
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={lots}
            onChange={(e) => setLots(e.target.value)}
            className="w-full rounded-md border-0 bg-gray-100 dark:bg-white/10 py-1.5 px-2 text-sm tabular-nums focus:ring-2 focus:ring-squawk-blue"
          />
          <span className="text-1.5xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
            lots (max {account.max_lots})
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="flex items-center gap-2 text-1.5xs text-gray-500 dark:text-gray-400">
            <input
              type="checkbox"
              checked={useSl}
              onChange={() => setUseSl(!useSl)}
              className="form-checkbox rounded text-squawk-blue"
            />
            Stop loss
            {useSl && (
              <input
                type="number"
                step="any"
                value={sl}
                onChange={(e) => setSl(e.target.value)}
                className="w-full rounded-md border-0 bg-gray-100 dark:bg-white/10 py-1 px-2 text-xs tabular-nums focus:ring-2 focus:ring-squawk-blue"
              />
            )}
          </label>
          <label className="flex items-center gap-2 text-1.5xs text-gray-500 dark:text-gray-400">
            <input
              type="checkbox"
              checked={useTp}
              onChange={() => setUseTp(!useTp)}
              className="form-checkbox rounded text-squawk-blue"
            />
            Take profit
            {useTp && (
              <input
                type="number"
                step="any"
                value={tp}
                onChange={(e) => setTp(e.target.value)}
                className="w-full rounded-md border-0 bg-gray-100 dark:bg-white/10 py-1 px-2 text-xs tabular-nums focus:ring-2 focus:ring-squawk-blue"
              />
            )}
          </label>
        </div>
      </div>

      {pendingOrderId && (
        <div className="rounded-md bg-squawk-blue/10 px-3 py-2 text-center text-xs text-squawk-blue">
          Sending order to your terminal…
        </div>
      )}

      {/* account strip */}
      <div className="mt-auto grid grid-cols-3 gap-2 border-t border-gray-200 dark:border-white/10 pt-2 text-1.5xs">
        <div>
          <span className="block text-gray-500 dark:text-gray-400">Balance</span>
          <span className="tabular-nums">
            {state ? `${state.balance.toLocaleString()} ${state.currency}` : "—"}
          </span>
        </div>
        <div>
          <span className="block text-gray-500 dark:text-gray-400">Equity</span>
          <span className="tabular-nums">{state ? state.equity.toLocaleString() : "—"}</span>
        </div>
        <div>
          <span className="block text-gray-500 dark:text-gray-400">Free margin</span>
          <span className="tabular-nums">{state ? state.margin_free.toLocaleString() : "—"}</span>
        </div>
      </div>
    </div>
  );
};

export default BrokerTicket;
